import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import './Contact.css';

const faqs = [
  { num: '01', title: 'What makes an icon authentic?', desc: 'Authentic icons follow canonical guidelines, use traditional materials and techniques, and are created with proper spiritual preparation including prayer and fasting.' },
  { num: '02', title: 'How long does a commission take?', desc: 'Personal icons typically take 4–8 weeks, while larger church projects can take 3–12 months depending on complexity and size.' },
  { num: '03', title: 'Can I request specific saints or subjects?', desc: 'Absolutely. I can create icons of any canonically recognized saint or traditional iconographic subject following proper theological guidelines.' },
  { num: '04', title: 'Do you offer payment plans?', desc: 'Yes, I offer flexible payment plans for larger commissions. Typically 50% deposit to begin, with the remainder due upon completion.' },
  { num: '05', title: 'What materials do you use?', desc: 'I use traditional materials including natural pigments, gold leaf, and properly prepared wood panels following ancient techniques.' },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    size: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    errorMessage: ''
  });

  const sizeOptions = [
    { value: '8x10', label: '8" x 10"', price: 175 },
    { value: '11x14', label: '11" x 14"', price: 220 },
    { value: '12x16', label: '12" x 16"', price: 250 },
    { value: '16x20', label: '16" x 20"', price: 275 },
    { value: '18x24', label: '18" x 24"', price: 320 },
    { value: 'custom', label: 'Larger Size (Custom Quote)', price: null }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const rate = window.pageYOffset * -0.1;
      document.documentElement.style.setProperty('--scroll-offset', `${rate}px`);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-in').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Helper function to get estimated price
  const getEstimatedPrice = () => {
    if (formData.projectType === 'personal-icon' || formData.projectType === 'family-icon') {
      const selectedSize = sizeOptions.find(option => option.value === formData.size);
      return selectedSize?.price ? `$${selectedSize.price}` : 'Custom Quote';
    }
    return 'Custom Quote';
  };

  // Helper function to format project type for display
  const formatProjectType = (type) => {
    const typeMap = {
      'personal-icon': 'Personal Icon',
      'family-icon': 'Family Icon',
      'church-commission': 'Church Commission',
      'iconostasis': 'Iconostasis Project',
      'consultation': 'Consultation'
    };
    return typeMap[type] || type;
  };

  // Reset form status messages after a delay
  useEffect(() => {
    if (formStatus.isSuccess || formStatus.isError) {
      const timer = setTimeout(() => {
        setFormStatus(prev => ({
          ...prev,
          isSuccess: false,
          isError: false,
          errorMessage: ''
        }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [formStatus.isSuccess, formStatus.isError]);

  // Enhanced form validation
  const validateForm = () => {
    const errors = [];
    
    if (!formData.name.trim()) {
      errors.push('Name is required');
    }
    
    if (!formData.email.trim()) {
      errors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    
    if (!formData.projectType) {
      errors.push('Please select a project type');
    }
    
    if ((formData.projectType === 'personal-icon' || formData.projectType === 'family-icon') && !formData.size) {
      errors.push('Please select an icon size');
    }
    
    if (!formData.message.trim()) {
      errors.push('Project details are required');
    } else if (formData.message.trim().length < 10) {
      errors.push('Please provide more detailed project information (at least 10 characters)');
    }
    
    return errors;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear any existing error messages when user starts typing
    if (formStatus.isError) {
      setFormStatus(prev => ({
        ...prev,
        isError: false,
        errorMessage: ''
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      projectType: '',
      size: '',
      message: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setFormStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        errorMessage: validationErrors.join('. ')
      });
      return;
    }
    
    // Set loading state
    setFormStatus({
      isSubmitting: true,
      isSuccess: false,
      isError: false,
      errorMessage: ''
    });

    try {      
      // Prepare template parameters for EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        project_type: formatProjectType(formData.projectType),
        size: formData.size || 'Not applicable',
        estimated_price: getEstimatedPrice(),
        message: formData.message,
        submission_date: new Date().toLocaleString()
      };

      console.log('Template params:', templateParams);

      // Send email using EmailJS
      const result = await emailjs.send(
        'service_1pazkqa',
        'template_jr8tm6p',
        templateParams,
        'LgFDBjwNqV8c65u6t'
      );

      if (result.status === 200) {
        // Success
        setFormStatus({
          isSubmitting: false,
          isSuccess: true,
          isError: false,
          errorMessage: ''
        });
        
        // Reset form after successful submission
        resetForm();
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('EmailJS Error:', error);
      
      // Determine error message based on error type
      let errorMessage = 'Failed to send your commission request. Please try again.';
      
      if (error.text) {
        if (error.text.includes('insufficient authentication scopes') || error.status === 412) {
          errorMessage = 'Email service configuration needs to be updated. Please contact us directly while we fix this.';
        } else if (error.text.includes('rate limit') || error.text.includes('429')) {
          errorMessage = 'Too many requests. Please wait a few minutes and try again.';
        } else if (error.text.includes('Invalid') || error.text.includes('403')) {
          errorMessage = 'Service temporarily unavailable. Please contact us directly.';
        } else if (error.text.includes('network') || error.text.includes('timeout')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network connection error. Please check your internet connection.';
      }
      
      setFormStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        errorMessage
      });
    }
  };

  return (
    <div className="contact">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>Commission Sacred Art</h1>
          <p className="lead">Begin your journey toward owning a piece of sacred iconography</p>
        </div>
        <div className="hero-scroll-indicator"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg></div>
      </section>

      {/* Contact Form Section */}
      <section className="page-section">
        <div className="contact-form-content">
          <div className="form-intro animate-in">
            <span className="section-label">Commission</span>
            <h2>Start Your Commission</h2>
            <p>
              Every icon begins with a conversation. Share your vision, spiritual needs,
              and preferences, and I will guide you through the sacred process of creating
              your custom iconography.
            </p>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            {/* Status Messages */}
            {formStatus.isSuccess && (
              <div className="form-message success-message">
                <div className="message-content">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  <span>Thank you! Your commission request has been sent successfully. We'll respond within 24-48 hours.</span>
                </div>
              </div>
            )}
            
            {formStatus.isError && (
              <div className="form-message error-message">
                <div className="message-content">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span>{formStatus.errorMessage}</span>
                </div>
                <p className="error-fallback">
                  You can also reach us directly at <a href="mailto:ramsis.icons@gmail.com">ramsis.icons@gmail.com</a> or call <a href="tel:+14253456483">(425) 345-6483</a>
                </p>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="projectType">Project Type *</label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a project type</option>
                  <option value="personal-icon">Personal Icon</option>
                  <option value="family-icon">Family Icon</option>
                  <option value="church-commission">Church Commission</option>
                  <option value="iconostasis">Iconostasis Project</option>
                  <option value="consultation">Consultation</option>
                </select>
              </div>
            </div>

            {/* Conditional Size Selection for Personal and Family Icons */}
            {(formData.projectType === 'personal-icon' || formData.projectType === 'family-icon') && (
              <div className="form-group size-selection">
                <label htmlFor="size">Icon Size *</label>
                <div className="size-options">
                  {sizeOptions.map((option) => (
                    <div key={option.value} className="size-option">
                      <input
                        type="radio"
                        id={`size-${option.value}`}
                        name="size"
                        value={option.value}
                        checked={formData.size === option.value}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor={`size-${option.value}`} className="size-option-label">
                        <span className="size-label">{option.label}</span>
                        <span className="size-price">
                          {option.price ? `$${option.price} + (Shipping etc)` : 'Cost will vary'}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="message">Project Details *</label>
              <textarea
                id="message"
                name="message"
                rows="6"
                value={formData.message}
                onChange={handleChange}
                placeholder="Please describe your vision, preferred saints or subjects, size requirements, timeline, and any specific spiritual or aesthetic preferences..."
                required
              ></textarea>
            </div>

            <button
              type="submit"
              className={`btn btn-primary ${formStatus.isSubmitting ? 'loading' : ''}`}
              disabled={formStatus.isSubmitting}
            >
              {formStatus.isSubmitting ? 'Sending Request...' : 'Send Commission Request'}
            </button>
          </form>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="page-section">
        <div className="section-content">
          <div className="contact-info-grid">
            <div className="contact-info-col animate-in">
              <span className="section-label">Reach Out</span>
              <h3>Get in Touch</h3>
              <ul className="contact-detail-list">
                <li>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                  <a href="mailto:ramsis.icons@gmail.com">ramsis.icons@gmail.com</a>
                </li>
                <li>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
                  <a href="tel:+14253456483">(425) 345-6483</a>
                </li>
                <li>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                  <span>By Appointment</span>
                </li>
              </ul>
            </div>

            <div className="contact-info-col animate-in" style={{ transitionDelay: '0.1s' }}>
              <span className="section-label">Timeline</span>
              <h3>Commission Process</h3>
              <ul className="timeline-list">
                <li>
                  <span className="tl-label">Personal Icons</span>
                  <span className="tl-duration">4–8 weeks</span>
                </li>
                <li>
                  <span className="tl-label">Church Projects</span>
                  <span className="tl-duration">3–12 months</span>
                </li>
                <li>
                  <span className="tl-label">Iconostasis</span>
                  <span className="tl-duration">6–18 months</span>
                </li>
              </ul>
            </div>

            <div className="contact-info-col animate-in" style={{ transitionDelay: '0.2s' }}>
              <span className="section-label">Follow</span>
              <h3>Stay Connected</h3>
              <div className="social-links-dark">
                <a href="https://instagram.com/ramsis_iconography" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="https://www.facebook.com/george.ramsis.56" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="mailto:ramsis.icons@gmail.com" aria-label="Email">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="page-section">
        <div className="section-content">
          <div className="section-header animate-in">
            <span className="section-label">Questions</span>
            <h2>Frequently Asked Questions</h2>
          </div>

          <div className="faq-grid">
            {faqs.map((item, i) => (
              <div key={i} className="numbered-card animate-in" style={{ transitionDelay: `${i * 0.07}s` }}>
                <span className="card-number">{item.num}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;